import React, {ChangeEvent, useEffect, useState, useCallback, useMemo} from 'react';
import {DocumentRead, DocumentType, ExchangeType, NotableExchange, PersonCredibility, TagType, getTranscriptMetadata} from '../../../../schemas';
import Button from "../../../../components/SharedComponents/Button/Button";
import {SlArrowDown, SlArrowRight, SlCalender} from "react-icons/sl";
import {FiNavigation, FiAlertTriangle} from "react-icons/fi";
import {EDITABLE_DOCUMENT_METADATA, ENABLE_ENTITY_TAGS} from "../../../../feature_flags";
import {PiBuildings, PiMapPin, PiUser, PiPackage, PiHash, PiCalendarBlank} from "react-icons/pi";
import { EntityType, PersonRole, EmailRole } from '../../../../routines/schemas';
import {useTagContext} from "../../../../contexts/tag_context";
import {useDocumentSpotlight, SpotlightMatchItem} from "../../../../contexts/document_spotlight_context";
import {matchPersonToSpeaker, namesMatch, buildSpeakerMap} from '../DocTypeDisplayText/shared/speaker-utils';
import {parseTranscriptLines} from '../DocTypeDisplayText/shared/transcript-parsing';
import {getWitnessCredibility, getDocument} from '../../../../api';
import {useEnhancedDocumentsContext} from "../../../../contexts/doc_contexts";

// ─── Field Config ────────────────────────────────────────────────────────────────
// Note: from_email / to_email / recipients removed — the Persons section below
// shows the same info with richer structure (name + email + email_role badge).
const fieldProperties = [
    {
        name: 'title',
        label: 'Title',
        types: [DocumentType.EMAIL, DocumentType.DOCUMENT, DocumentType.TRANSCRIPT],
        additionalClasses: 'max-w-1/2',
        inputType: 'text'
    },
    {
        name: 'summary',
        label: 'Summary',
        types: [DocumentType.EMAIL, DocumentType.DOCUMENT, DocumentType.TRANSCRIPT],
        additionalClasses: 'w-full',
        inputType: 'textarea'
    },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────────
const entityColors: Record<EntityType, string> = {
    [EntityType.organization]: "bg-emerald-100 text-emerald-800 border-emerald-200",
    [EntityType.location]: "bg-amber-100 text-amber-800 border-amber-200",
    [EntityType.item]: "bg-violet-100 text-violet-800 border-violet-200",
    [EntityType.topic]: "bg-cyan-100 text-cyan-800 border-cyan-200",
    [EntityType.person]: "bg-indigo-100 text-indigo-800 border-indigo-200",
    [EntityType.event]: "bg-slate-100 text-slate-800 border-slate-200",
};

const entityIcons: Record<EntityType, React.ReactNode> = {
    [EntityType.organization]: <PiBuildings className="text-emerald-500"/>,
    [EntityType.location]: <PiMapPin className="text-amber-500"/>,
    [EntityType.item]: <PiPackage className="text-violet-500"/>,
    [EntityType.topic]: <PiHash className="text-cyan-500"/>,
    [EntityType.person]: <PiUser className="text-indigo-500"/>,
    [EntityType.event]: <PiCalendarBlank className="text-slate-500"/>,
};

// Person role styling
const personRoleColors: Record<PersonRole, string> = {
    // General roles
    [PersonRole.PARTICIPANT]: "bg-indigo-100 text-indigo-800 border-indigo-200",
    [PersonRole.MENTIONED]: "bg-slate-100 text-slate-700 border-slate-200",
    [PersonRole.SIGNER]: "bg-teal-100 text-teal-800 border-teal-200",
    [PersonRole.AUTHOR]: "bg-purple-100 text-purple-800 border-purple-200",
    // Legal document-specific roles
    [PersonRole.PARTY]: "bg-blue-100 text-blue-800 border-blue-200",
    [PersonRole.WITNESS]: "bg-cyan-100 text-cyan-800 border-cyan-200",
    [PersonRole.NOTARY]: "bg-amber-100 text-amber-800 border-amber-200",
    [PersonRole.ATTORNEY]: "bg-rose-100 text-rose-800 border-rose-200",
    [PersonRole.JUDGE]: "bg-red-100 text-red-800 border-red-200",
    [PersonRole.PLAINTIFF]: "bg-orange-100 text-orange-800 border-orange-200",
    [PersonRole.DEFENDANT]: "bg-yellow-100 text-yellow-800 border-yellow-200",
    [PersonRole.AUDITOR]: "bg-lime-100 text-lime-800 border-lime-200",
    [PersonRole.PREPARER]: "bg-emerald-100 text-emerald-800 border-emerald-200",
    [PersonRole.REVIEWER]: "bg-sky-100 text-sky-800 border-sky-200",
    [PersonRole.ADDRESSEE]: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
};

const emailRoleLabels: Record<EmailRole, string> = {
    [EmailRole.SENDER]: "From",
    [EmailRole.RECIPIENT]: "To",
    [EmailRole.CC]: "CC",
    [EmailRole.BCC]: "BCC",
};

// Examination type styling
const examTypeStyles: Record<string, { bg: string; border: string; text: string; label: string }> = {
    DIRECT: {bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'Direct'},
    CROSS: {bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', label: 'Cross'},
    REDIRECT: {bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', label: 'Redirect'},
    RECROSS: {bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Recross'},
    VOIR_DIRE: {bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', label: 'Voir Dire'},
    UNKNOWN: {bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', label: 'Examination'},
};

function getExamStyle(type: string) {
    return examTypeStyles[type] ?? examTypeStyles.UNKNOWN;
}

// ─── Component ───────────────────────────────────────────────────────────────────
type DocumentViewProps = {
    doc: DocumentRead;
};

const MetadataPalette: React.FC<DocumentViewProps> = ({doc}) => {
    const [isPaletteOpen, setIsPaletteOpen] = useState(true);
    const [formData, setFormData] = useState<DocumentRead>(doc);

    // Only use tag context when entity tags are enabled
    const tagContext = useTagContext();
    const tagFilters = tagContext?.tagFilters;
    const toggleEntityTagFilter = tagContext?.toggleEntityTagFilter;

    // Spotlight for examination block navigation
    const spotlightCtx = useDocumentSpotlight();
    const docCtx = useEnhancedDocumentsContext();
    const transcriptMeta = formData.type === DocumentType.TRANSCRIPT
        ? getTranscriptMetadata(formData) : undefined;
    const examBlocks = transcriptMeta?.examination_index;

    // Pre-parse transcript lines and speaker map for speaker spotlights.
    // Only computed for transcripts; memoized so repeated person clicks are instant.
    const tLines = useMemo(() => {
        if (formData.type !== DocumentType.TRANSCRIPT || !formData.body) return null;
        return parseTranscriptLines(formData.body);
    }, [formData.body, formData.type]);

    const speakerMap = useMemo(() => {
        if (!tLines || !examBlocks) return null;
        return buildSpeakerMap(tLines, examBlocks, transcriptMeta?.deponent_name ?? undefined);
    }, [tLines, examBlocks, transcriptMeta?.deponent_name]);

    // ── Witness credibility — fetch profiles for any persons that have a tag_id ───
    // Lazy: triggers when palette is open and we have person tag ids. Aggregated
    // across the whole group, not just this doc.
    const [credibility, setCredibility] = useState<Record<string, PersonCredibility>>({});

    const personTagIds = useMemo(() => {
        return (formData.summary_info?.persons ?? [])
            .map(p => p.tag_id)
            .filter((id): id is string => !!id);
    }, [formData.summary_info?.persons]);

    useEffect(() => {
        if (!isPaletteOpen || personTagIds.length === 0) return;
        let cancelled = false;
        getWitnessCredibility(personTagIds)
            .then(profiles => { if (!cancelled) setCredibility(profiles); })
            .catch(err => { console.warn('[MetadataPalette] credibility fetch failed:', err); });
        return () => { cancelled = true; };
    }, [isPaletteOpen, personTagIds.join(',')]);  // join to memoize array contents

    /** Build matchItems for a person: their speaker lines + lines where they&apos;re mentioned by name. */
    const buildSpeakerMatchItems = useCallback((personName: string, role: 'witness' | 'attorney'): SpotlightMatchItem[] => {
        if (!tLines || !speakerMap) return [];
        const items: SpotlightMatchItem[] = [];
        const captured = new Set<number>();

        // Pass 1: Speaker lines (where this person is actually the one speaking)
        const expectedSpeaker = role === 'witness' ? 'A' : 'Q';
        for (const [lineIdx, names] of speakerMap) {
            const line = tLines[lineIdx];
            if (line.speaker !== expectedSpeaker) continue;
            const resolvedName = role === 'witness' ? names.a : names.q;
            if (!resolvedName || !namesMatch(personName, resolvedName)) continue;
            const text = line.content.length > 200 ? line.content.slice(0, 200) : line.content;
            items.push({text, lineIndex: lineIdx});
            captured.add(lineIdx);
        }

        // Pass 2: Name mentions — extract a search-friendly last name and find lines
        // where it appears (excluding speaker designations to avoid false positives).
        let normalized = personName.toLowerCase().trim();
        const PREFIX_RE = /^(mr|mrs|ms|dr|judge|hon|attorney|atty|esq|detective|det|officer|ofc|sgt|sergeant|lt|lieutenant|capt|captain|chief|prof|professor|rev)\.?\s+/i;
        let prev = '';
        while (normalized !== prev) {
            prev = normalized;
            normalized = normalized.replace(PREFIX_RE, '').trim();
        }
        const nameParts = normalized.split(/\s+/).filter(p => p.length > 0);
        // Strip trailing suffixes (Jr, Sr, II, III, etc.)
        while (nameParts.length > 1 && /^(jr|sr|ii|iii|iv|v|esq)\.?$/i.test(nameParts[nameParts.length - 1])) {
            nameParts.pop();
        }
        const searchName = nameParts[nameParts.length - 1] || '';

        if (searchName.length >= 4) {
            const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const re = new RegExp(`\\b${escapeRe(searchName)}\\b`, 'i');
            for (let i = 0; i < tLines.length; i++) {
                if (captured.has(i)) continue;
                const line = tLines[i];
                if (line.isEmpty || line.isPageBreak) continue;
                // Search the rest after the speaker designation if present, so we
                // don&apos;t match "MR. NADEAU:" when looking for witness Nadeau
                const searchable = line.speaker ? line.speakerRest : line.content;
                if (!re.test(searchable)) continue;
                const text = line.content.length > 200 ? line.content.slice(0, 200) : line.content;
                items.push({text, lineIndex: i});
                captured.add(i);
            }
        }

        // Sort by line index so the scrubber walks through them in document order
        items.sort((a, b) => a.lineIndex - b.lineIndex);
        return items;
    }, [tLines, speakerMap]);

    useEffect(() => {
        setFormData(doc);
    }, [doc]);


    // Toggle entity tag filter when clicking a chip (adds to filter panel)
    const handleEntityClick = useCallback((tagId: string | null | undefined) => {
        if (!ENABLE_ENTITY_TAGS || !toggleEntityTagFilter || !tagId) return;
        toggleEntityTagFilter(tagId);
    }, [toggleEntityTagFilter]);

    // Check if an entity tag is currently in the filter (highlighted)
    const isEntityHighlighted = useCallback((tagId: string | null | undefined) => {
        if (!ENABLE_ENTITY_TAGS || !tagId) return false;
        return tagFilters?.has(tagId) ?? false;
    }, [tagFilters]);

    const handleChange = <T extends HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        e: ChangeEvent<T>,
        fieldName: keyof DocumentRead
    ) => {
        const {value} = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [fieldName]: value,
        }));
    };

    const ArrowButton = () => (
        isPaletteOpen ?
            <SlArrowDown className="m-1 rounded text-slate-600 hover:text-slate-900"/> :
            <SlArrowRight className="m-1 rounded text-slate-600 hover:text-slate-900"/>
    );

    const summaryInfo = formData.summary_info;

    return (
        <div className="rounded-lg border border-slate-300 bg-white shadow-sm w-full">
            {/* Header toggle */}
            <button
                type="button"
                onClick={() => setIsPaletteOpen((cur) => !cur)}
                className="flex w-full items-center justify-between px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
                <span className="flex items-center gap-2">
                    <ArrowButton/>
                    <span>Document Metadata</span>
                </span>
                {!isPaletteOpen && <span className="text-xs text-slate-400">Click to expand</span>}
            </button>

            {/* Collapsible body */}
            <div
                className={`overflow-hidden transition-all duration-300 ${
                    isPaletteOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="px-4 py-3 space-y-5 text-sm text-slate-800">

                    {/* Editable fields */}
                    {fieldProperties
                        .filter(({types}) => types.includes(formData.type))
                        .map(({name, label, additionalClasses, inputType}) => {
                            const fieldValue = formData[name];
                            const stringValue =
                                fieldValue !== undefined && fieldValue !== null
                                    ? fieldValue.toString()
                                    : "";

                            return (
                                <div key={name} className={`flex flex-col ${additionalClasses}`}>
                                    <label
                                        htmlFor={name}
                                        className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500"
                                    >
                                        {label}
                                    </label>
                                    {EDITABLE_DOCUMENT_METADATA ? (
                                        inputType === "text" ? (
                                            <input
                                                id={name}
                                                name={name}
                                                type="text"
                                                value={stringValue}
                                                onChange={(e) => handleChange(e, name)}
                                                className="rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                            />
                                        ) : (
                                            <textarea
                                                id={name}
                                                name={name}
                                                value={stringValue}
                                                onChange={(e) => handleChange(e, name)}
                                                rows={3}
                                                className="rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                            />
                                        )
                                    ) : (
                                        <div className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1 text-slate-700">
                                            {stringValue || <span className="italic text-slate-400">—</span>}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                    {/* EXAMINATIONS SECTION (transcripts only) */}
                    {examBlocks && examBlocks.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                Examinations
                            </h4>
                            <div className="space-y-1.5">
                                {examBlocks
                                    .slice()
                                    .sort((a, b) => (a.pdf_page_start ?? a.transcript_page_start ?? 0)
                                        - (b.pdf_page_start ?? b.transcript_page_start ?? 0))
                                    .map((block, idx) => {
                                        const style = getExamStyle(block.examination_type);
                                        const hasPdfMapping = block.pdf_page_start != null && block.pdf_page_start !== block.transcript_page_start;
                                        const pageLabel = block.transcript_page_start != null
                                            ? block.transcript_page_end != null
                                                ? `Tr. pp. ${block.transcript_page_start}\u2013${block.transcript_page_end}` + (hasPdfMapping ? ` (PDF p. ${block.pdf_page_start})` : '')
                                                : `Tr. p. ${block.transcript_page_start}+` + (hasPdfMapping ? ` (PDF p. ${block.pdf_page_start})` : '')
                                            : null;
                                        const jumpPage = block.pdf_page_start ?? block.transcript_page_start;

                                        return (
                                            <button
                                                key={`exam-${idx}`}
                                                type="button"
                                                onClick={() => {
                                                    if (jumpPage != null) {
                                                        spotlightCtx?.jumpToSpotlight({
                                                            text: `${style.label} Examination: ${block.witness ?? block.attorney ?? 'Unknown'}`,
                                                            source: `${style.label} — p. ${jumpPage}`,
                                                            pageHint: jumpPage,
                                                            kind: 'page',
                                                        });
                                                    }
                                                }}
                                                className={`flex items-center gap-2 w-full text-left px-2.5 py-2 rounded-md border ${style.border} ${style.bg} hover:shadow-sm transition-shadow cursor-pointer`}
                                            >
                                                <span className={`text-[10px] font-bold uppercase tracking-wider ${style.text} shrink-0`}>
                                                    {style.label}
                                                </span>
                                                <span className="text-xs text-slate-700 truncate flex-1">
                                                    {block.witness && (
                                                        <span className="font-medium">{block.witness}</span>
                                                    )}
                                                    {block.witness && block.attorney && (
                                                        <span className="text-slate-400 mx-1">by</span>
                                                    )}
                                                    {block.attorney && (
                                                        <span>{block.attorney}</span>
                                                    )}
                                                    {!block.witness && !block.attorney && (
                                                        <span className="italic text-slate-400">Unknown</span>
                                                    )}
                                                </span>
                                                {pageLabel && (
                                                    <span className="text-[10px] text-slate-400 shrink-0">
                                                        {pageLabel}
                                                    </span>
                                                )}
                                                <FiNavigation className="w-3 h-3 text-slate-400 shrink-0"/>
                                            </button>
                                        );
                                    })}
                            </div>
                        </div>
                    )}

                    {/* PERSONS SECTION */}
                    {summaryInfo?.persons && summaryInfo.persons.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                Persons
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {summaryInfo.persons.map((person, idx) => {
                                    const hasTag = !!person.tag_id;
                                    const isHighlighted = isEntityHighlighted(person.tag_id);
                                    const hasSpotlight = !!spotlightCtx?.findByText(person.name);
                                    const roleLabel = person.role ? person.role.replace('_', ' ') : 'Person';
                                    return (
                                        <div
                                            key={`${person.name}-${idx}`}
                                            onClick={() => {
                                                // Match person to speaker for role labeling
                                                const speakerMatch = matchPersonToSpeaker(
                                                    person.name, examBlocks, transcriptMeta?.deponent_name ?? undefined,
                                                );
                                                const speakerRole = speakerMatch?.role === 'witness' ? 'A (Witness)'
                                                    : speakerMatch?.role === 'attorney' ? 'Q (Attorney)' : roleLabel;
                                                const source = speakerMatch
                                                    ? `${speakerRole}: ${person.name}`
                                                    : `${roleLabel}: ${person.name}`;

                                                // Speaker spotlight: pre-compute all speaker lines
                                                if (speakerMatch) {
                                                    const matchItems = buildSpeakerMatchItems(person.name, speakerMatch.role);
                                                    spotlightCtx?.jumpToSpotlight({
                                                        text: person.name,
                                                        source,
                                                        kind: 'speaker',
                                                        matchItems,
                                                    });
                                                } else {
                                                    // Text spotlight: search for literal name
                                                    spotlightCtx?.jumpToSpotlight({
                                                        text: person.name,
                                                        source,
                                                    });
                                                }
                                                // Also toggle entity tag filter if available
                                                if (hasTag) handleEntityClick(person.tag_id);
                                            }}
                                            className={`rounded-md border px-2.5 py-1.5 text-xs ${personRoleColors[person.role]} ${
                                                'cursor-pointer hover:shadow-md transition-shadow'
                                            } ${isHighlighted ? 'ring-2 ring-indigo-400 ring-offset-1' : ''} ${
                                                hasSpotlight ? 'ring-2 ring-amber-400 ring-offset-1' : ''
                                            }`}
                                            title="Click to find in document"
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <PiUser className="w-3.5 h-3.5 flex-shrink-0"/>
                                                <span className="font-medium">{person.name}</span>
                                                {person.email_role && (
                                                    <span className="opacity-70 text-[10px]">
                                                        ({emailRoleLabels[person.email_role]})
                                                    </span>
                                                )}
                                            </div>
                                            {(person.email || person.organization || person.title) && (
                                                <div className="text-[10px] opacity-70 mt-0.5 ml-5">
                                                    {[person.email, person.organization, person.title].filter(Boolean).join(' · ')}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* WITNESS CREDIBILITY SECTION (transcripts only, when issues exist) */}
                    {(() => {
                        const flagged = (formData.summary_info?.persons ?? [])
                            .map(person => {
                                const profile = person.tag_id ? credibility[person.tag_id] : undefined;
                                if (!profile) return null;
                                const incCount = profile.counts_by_type?.[ExchangeType.INCONSISTENCY] ?? 0;
                                const credCount = profile.counts_by_type?.[ExchangeType.CREDIBILITY] ?? 0;
                                if (incCount + credCount === 0) return null;
                                return {person, profile, incCount, credCount};
                            })
                            .filter((x): x is NonNullable<typeof x> => x !== null);

                        if (flagged.length === 0) return null;

                        return (
                            <div>
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                                    <FiAlertTriangle className="text-amber-500"/>
                                    Witness Credibility
                                </h4>
                                <div className="space-y-2">
                                    {flagged.map(({person, profile, incCount, credCount}) => (
                                        <div
                                            key={`cred-${person.tag_id}`}
                                            className="rounded-md border border-amber-200 bg-amber-50/50 p-2.5"
                                        >
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <PiUser className="w-3.5 h-3.5 text-amber-700 flex-shrink-0"/>
                                                <span className="text-xs font-medium text-slate-800">{person.name}</span>
                                                {incCount > 0 && (
                                                    <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-rose-100 text-rose-700 border border-rose-200">
                                                        {incCount} {incCount === 1 ? 'inconsistency' : 'inconsistencies'}
                                                    </span>
                                                )}
                                                {credCount > 0 && (
                                                    <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-orange-100 text-orange-700 border border-orange-200">
                                                        {credCount} credibility {credCount === 1 ? 'flag' : 'flags'}
                                                    </span>
                                                )}
                                                <span className="ml-auto text-[10px] text-slate-500">
                                                    {profile.total_exchanges} total exchanges
                                                </span>
                                            </div>
                                            <div className="space-y-1">
                                                {profile.notable_exchanges.map((ex: NotableExchange) => (
                                                    <button
                                                        key={ex.reference_id}
                                                        type="button"
                                                        onClick={async () => {
                                                            const spotlightReq = {
                                                                text: ex.quote,
                                                                source: `${ex.exchange_type}: ${person.name}`,
                                                                pageHint: ex.page_start ?? undefined,
                                                            };
                                                            if (ex.doc_id && ex.doc_id !== doc.id) {
                                                                // Cross-document: navigate to the target doc then spotlight
                                                                spotlightCtx?.queueJump(spotlightReq);
                                                                const targetDoc = await getDocument(ex.doc_id);
                                                                docCtx?.setDocument(targetDoc);
                                                            } else {
                                                                spotlightCtx?.jumpToSpotlight(spotlightReq);
                                                            }
                                                        }}
                                                        className={`w-full text-left rounded border px-2 py-1.5 text-[11px] text-slate-700 transition-colors ${
                                                            ex.doc_id && ex.doc_id !== doc.id
                                                                ? 'border-blue-200 bg-blue-50/30 hover:bg-blue-50 hover:border-blue-300'
                                                                : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-amber-300'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-1.5 mb-0.5">
                                                            <span className={`px-1 py-0.5 text-[9px] font-semibold rounded ${
                                                                ex.exchange_type === ExchangeType.INCONSISTENCY
                                                                    ? 'bg-rose-100 text-rose-700'
                                                                    : 'bg-orange-100 text-orange-700'
                                                            }`}>
                                                                {ex.exchange_type}
                                                            </span>
                                                            {ex.doc_title && (
                                                                <span className={`text-[10px] truncate ${
                                                                    ex.doc_id && ex.doc_id !== doc.id ? 'text-blue-500' : 'text-slate-400'
                                                                }`}>
                                                                    {ex.doc_id && ex.doc_id !== doc.id && <FiNavigation className="inline w-2.5 h-2.5 mr-0.5" />}
                                                                    {ex.doc_title}
                                                                </span>
                                                            )}
                                                            {ex.page_start != null && (
                                                                <span className="text-slate-400 text-[10px] ml-auto">
                                                                    p. {ex.page_start}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="line-clamp-2 italic text-slate-600">
                                                            &ldquo;{ex.quote}&rdquo;
                                                        </div>
                                                        {ex.explanation && (
                                                            <div className="mt-0.5 text-[10px] text-slate-500 line-clamp-2">
                                                                {ex.explanation}
                                                            </div>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* ENTITY SECTION */}
                    {summaryInfo?.entities && summaryInfo.entities.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                Entities
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {summaryInfo.entities.map((entity, idx) => {
                                    // Resolve tag_id by name from tagMap — the stored tag_id
                                    // in summary_info can be stale from embedding dedup bugs
                                    const resolvedTagId = (() => {
                                        if (!tagContext) return entity.tag_id;
                                        // Try stored tag_id first
                                        if (entity.tag_id && tagContext.tagMap.get(entity.tag_id)?.tag_name?.toLowerCase() === entity.name.toLowerCase()) {
                                            return entity.tag_id;
                                        }
                                        // Fall back to name lookup
                                        for (const [id, tag] of tagContext.tagMap) {
                                            if (tag.tag_name.toLowerCase() === entity.name.toLowerCase() && tag.type === TagType.ENTITY) {
                                                return id;
                                            }
                                        }
                                        return entity.tag_id;
                                    })();
                                    const hasTag = !!resolvedTagId;
                                    const isHighlighted = isEntityHighlighted(resolvedTagId);
                                    const hasSpotlight = !!spotlightCtx?.findByText(entity.name);
                                    return (
                                        <span
                                            key={`${entity.name}-${idx}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Spotlight: search for entity name in document
                                                spotlightCtx?.jumpToSpotlight({
                                                    text: entity.name,
                                                    source: `${entity.type}: ${entity.name}`,
                                                });
                                                // Also toggle entity tag filter if available
                                                if (hasTag) handleEntityClick(resolvedTagId);
                                            }}
                                            className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border ${entityColors[entity.type]} shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
                                                isHighlighted ? 'ring-2 ring-blue-400 ring-offset-1' : ''
                                            } ${hasSpotlight ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                                            title="Click to find in document"
                                        >
                                            {entityIcons[entity.type]}
                                            {entity.name}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* EVENT SECTION */}
                    {summaryInfo?.events && summaryInfo.events.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                Events
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {summaryInfo.events.map((event, idx) => {
                                    const hasTag = !!event.tag_id;
                                    const isHighlighted = isEntityHighlighted(event.tag_id);
                                    const hasEvidence = !!event.evidence?.quote;
                                    const hasSpotlight = hasEvidence && !!spotlightCtx?.findByText(event.evidence!.quote);
                                    return (
                                        <div
                                            key={`${event.name}-${idx}`}
                                            onClick={() => {
                                                // Spotlight: jump to evidence quote or search for event name
                                                const spotlightText = event.evidence?.quote || event.name;
                                                spotlightCtx?.jumpToSpotlight({
                                                    text: spotlightText,
                                                    source: `Event: ${event.name}`,
                                                    pageHint: event.evidence?.page_start ?? undefined,
                                                });
                                                // Also toggle entity tag filter if available
                                                if (hasTag) handleEntityClick(event.tag_id);
                                            }}
                                            className={`rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs cursor-pointer hover:shadow-md transition-shadow ${
                                                isHighlighted ? 'ring-2 ring-slate-400 ring-offset-1' : ''
                                            } ${hasSpotlight ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                                            title="Click to find in document"
                                        >
                                            <div className="flex items-center gap-2">
                                                <PiCalendarBlank className="w-3.5 h-3.5 text-slate-500 flex-shrink-0"/>
                                                <span className="font-medium text-slate-800">{event.name}</span>
                                                {event.date && (
                                                    <span className="flex items-center gap-1 text-slate-500 text-[10px]">
                                                        <SlCalender className="w-2.5 h-2.5"/>
                                                        {event.date}
                                                    </span>
                                                )}
                                            </div>
                                            {event.description && (
                                                <p className="text-[10px] text-slate-600 mt-0.5">{event.description}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {EDITABLE_DOCUMENT_METADATA && (
                        <div className="pt-2">
                            <Button size="small" type="submit">
                                Save Changes
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MetadataPalette;
